import React from 'react';
import { Card, CardContent } from '@tyndo/ui-kit';

/**
 * Shared chrome for the pre-session auth pages (signup, verify, forgot/reset
 * password). Mirrors the LoginPage card + gradient so the whole native-auth
 * flow reads as one surface.
 */
export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background:
          'radial-gradient(120% 80% at 50% 0%, var(--color-brand-primary) 0%, var(--color-background) 65%, var(--color-surface-sunken) 100%)',
      }}
    >
      <div className="w-full max-w-md">
        <Card variant="elevated" className="shadow-[var(--shadow-overlay)] rounded-2xl border-0">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-xl mb-4 shadow-[var(--shadow-md)]">
                <span className="text-2xl font-bold text-primary-foreground">T</span>
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-foreground">Tyndo</h1>
              {title && (
                <p className="text-lg font-medium text-foreground mt-3">{title}</p>
              )}
              {subtitle && <p className="t-eyebrow mt-2">{subtitle}</p>}
            </div>
            {children}
          </CardContent>
        </Card>
        <div className="text-center text-foreground/60 text-xs mt-6">
          <p>&copy; {new Date().getFullYear()} Tyndo by XarvisLabs. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

/** Critical-state inline error banner — matches LoginPage's error styling. */
export function AuthError({ message }: { message: string }) {
  return (
    <div className="bg-alarm-critical/10 border border-alarm-critical/30 text-alarm-critical px-4 py-3 rounded-md text-sm">
      {message}
    </div>
  );
}

/** Advisory/info inline banner for confirmations. */
export function AuthNotice({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-alarm-advisory/10 border border-alarm-advisory/30 text-foreground px-4 py-3 rounded-md text-sm">
      {children}
    </div>
  );
}
